import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI, twoFAAPI } from '../services/api';
const logo = process.env.PUBLIC_URL + '/pelicanplanner-logo.png';
const bgImage = process.env.PUBLIC_URL + '/japanesefishingvillaige.png';

function Login({ onLogin }) {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [suspended, setSuspended] = useState(false);
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [step, setStep] = useState('login'); // login | reset-email | reset-code
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Forced password change (admin reset)
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [preAuthToken, setPreAuthToken] = useState('');
  const [tfaCode, setTfaCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);

  const clearError = () => setError('');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // ── Normal login ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setLoading(true);
    try {
      const response = await authAPI.login(formData);
      const { token, user, requires_2fa, requires_password_change, pre_auth_token } = response.data;
      if (requires_password_change) {
        setPreAuthToken(pre_auth_token);
        setRequiresPasswordChange(true);
      } else if (requires_2fa) {
        setPreAuthToken(pre_auth_token);
        setRequires2FA(true);
      } else {
        onLogin(token, user);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'suspended') {
        setError(data.message);
        setSuspended(true);
      } else {
        setError(data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot password — step 1: send code ──────────────────────────────────
  const handleRequestReset = async (e) => {
    e.preventDefault();
    clearError();
    setLoading(true);
    try {
      await authAPI.requestReset({ email: resetEmail });
      setStep('reset-code');
    } catch {
      setError('Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot password — step 2: verify code + set new password ─────────────
  const handleConfirmReset = async (e) => {
    e.preventDefault();
    clearError();
    if (newPassword !== confirmPassword) return setError('Passwords do not match.');
    if (newPassword.length < 8) return setError('Password must be at least 8 characters.');
    setLoading(true);
    try {
      await authAPI.confirmReset({ email: resetEmail, code: resetCode, newPassword });
      setResetSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Forced password change (admin reset) ──────────────────────────────────
  const handleSetPassword = async (e) => {
    e.preventDefault();
    clearError();
    if (newPassword !== confirmPassword) return setError('Passwords do not match.');
    if (newPassword.length < 8) return setError('Password must be at least 8 characters.');
    setLoading(true);
    try {
      const response = await authAPI.setPassword({ newPassword, preAuthToken });
      const { token, user, requires_2fa, pre_auth_token } = response.data;
      if (requires_2fa) {
        setPreAuthToken(pre_auth_token);
        setRequiresPasswordChange(false);
        setRequires2FA(true);
      } else {
        onLogin(token, user);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── 2FA verify ────────────────────────────────────────────────────────────
  const handle2FASubmit = async (e) => {
    e.preventDefault();
    clearError();
    setLoading(true);
    try {
      const response = await twoFAAPI.verify({ code: tfaCode, rememberDevice, preAuthToken });
      onLogin(response.data.token, response.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const subtitle = {
    login: 'Sign in to your Family Nest',
    'reset-email': 'Reset Your Password',
    'reset-code': 'Enter Your Reset Code',
  }[step] || (requiresPasswordChange ? 'Create a New Password' : requires2FA ? 'Two-Factor Authentication' : 'Sign in to your Family Nest');

  return (
    <div className="auth-container" style={{ backgroundImage: `url(${bgImage})` }}>
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src={logo} alt="Pelican Planner"
            style={{
              width: '96px', height: '96px', margin: '0 auto 16px',
              borderRadius: '16px', display: 'block', objectFit: 'cover',
              boxShadow: '0 4px 16px rgba(255,107,107,0.3)'
            }} />
          <h1 className="auth-title">Pelican Planner</h1>
          <p className="auth-subtitle">{subtitle}</p>
        </div>

        {error && (
          <div className="alert alert-error" style={suspended ? {
            borderLeft: '4px solid #ef4444', background: 'rgba(239,68,68,0.08)',
            padding: '14px 16px', borderRadius: '8px', lineHeight: '1.5'
          } : {}}>
            {suspended && <div style={{ fontWeight: 700, marginBottom: '4px' }}>Account Suspended</div>}
            {error}
          </div>
        )}

        {/* ── Normal login ── */}
        {step === 'login' && !requiresPasswordChange && !requires2FA && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="username">Username</label>
              <input
                type="text" id="username" name="username" className="form-input"
                value={formData.username} onChange={handleChange}
                required autoComplete="username" placeholder="Enter your username"
              />
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label className="form-label" htmlFor="password" style={{ margin: 0 }}>Password</label>
                <button type="button"
                  onClick={() => { setStep('reset-email'); clearError(); setSuspended(false); }}
                  style={{ background: 'none', border: 'none', color: 'var(--accent-coral)', fontSize: '13px', cursor: 'pointer', padding: 0 }}>
                  Forgot password?
                </button>
              </div>
              <input
                type="password" id="password" name="password" className="form-input"
                value={formData.password} onChange={handleChange}
                required autoComplete="current-password" placeholder="Enter your password"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}
              style={{ marginTop: '8px', padding: '12px' }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {/* ── Forgot password step 1: enter email ── */}
        {step === 'reset-email' && (
          <form onSubmit={handleRequestReset}>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px', textAlign: 'center' }}>
              Enter the email address on your account and we'll send you a reset code.
            </p>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email" className="form-input"
                value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                placeholder="your@email.com" required autoFocus
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}
              style={{ padding: '12px' }}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <button type="button" className="btn btn-secondary btn-block"
              onClick={() => { setStep('login'); clearError(); }}
              style={{ marginTop: '10px' }}>
              Back to Sign In
            </button>
          </form>
        )}

        {/* ── Forgot password step 2: enter code + new password ── */}
        {step === 'reset-code' && !resetSuccess && (
          <form onSubmit={handleConfirmReset}>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px', textAlign: 'center' }}>
              Check your email for the 6-digit code. It expires in 15 minutes.
            </p>
            <div className="form-group">
              <label className="form-label">Reset Code</label>
              <input
                type="text" className="form-input"
                value={resetCode} onChange={e => setResetCode(e.target.value)}
                placeholder="000000" maxLength={6} autoFocus required
                style={{ textAlign: 'center', fontSize: '22px', letterSpacing: '6px', fontFamily: 'monospace' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password" className="form-input"
                value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="At least 8 characters" required autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password" className="form-input"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat your new password" required autoComplete="new-password"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}
              style={{ padding: '12px' }}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <button type="button"
              onClick={() => { setStep('reset-email'); clearError(); }}
              style={{ marginTop: '10px', width: '100%', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '13px', cursor: 'pointer' }}>
              Resend code
            </button>
          </form>
        )}

        {/* ── Reset success ── */}
        {step === 'reset-code' && resetSuccess && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✓</div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px', marginBottom: '24px' }}>
              Password reset successfully!
            </p>
            <button className="btn btn-primary btn-block"
              onClick={() => { setStep('login'); setResetSuccess(false); setNewPassword(''); setConfirmPassword(''); setResetCode(''); setResetEmail(''); }}
              style={{ padding: '12px' }}>
              Back to Sign In
            </button>
          </div>
        )}

        {/* ── Forced password change (admin reset) ── */}
        {requiresPasswordChange && (
          <form onSubmit={handleSetPassword}>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px', textAlign: 'center' }}>
              Your password was reset by an administrator. Please create a new password to continue.
            </p>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password" className="form-input"
                value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="At least 8 characters" autoFocus required autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password" className="form-input"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat your new password" required autoComplete="new-password"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}
              style={{ marginTop: '8px', padding: '12px' }}>
              {loading ? 'Saving...' : 'Set New Password'}
            </button>
          </form>
        )}

        {/* ── 2FA ── */}
        {requires2FA && (
          <form onSubmit={handle2FASubmit}>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px', textAlign: 'center' }}>
              Enter the 6-digit code from your authenticator app.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="tfaCode">Authentication Code</label>
              <input
                type="text" id="tfaCode" className="form-input"
                value={tfaCode} onChange={e => setTfaCode(e.target.value)}
                placeholder="000 000" maxLength={7} autoComplete="one-time-code"
                autoFocus required
                style={{ textAlign: 'center', fontSize: '22px', letterSpacing: '6px', fontFamily: 'monospace' }}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px', cursor: 'pointer' }}>
              <input type="checkbox" checked={rememberDevice}
                onChange={e => setRememberDevice(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              Remember this device for 30 days
            </label>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}
              style={{ padding: '12px' }}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button type="button" className="btn btn-secondary btn-block"
              onClick={() => { setRequires2FA(false); setTfaCode(''); clearError(); }}
              style={{ marginTop: '10px' }}>
              Back
            </button>
          </form>
        )}

        {step === 'login' && !requires2FA && !requiresPasswordChange && (
          <p className="text-center mt-3" style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
            Don't have an account?{' '}
            <Link to="/register" className="auth-link">Create one</Link>
          </p>
        )}
      </div>
    </div>
  );
}

export default Login;
